import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { featureFlagOverrides } from './feature-flags';
import type { FeatureFlagOverrides } from './types';
import {
  deleteFeatureFlagOverridesQueryParams,
  getFeatureFlagOverridesFromInitialURL,
} from './utils';

/**
 * Returns the feature flag overrides with the correct priority.
 *
 * 1. Query overrides (?features.<feature-name>=<feature-value>).
 * 2. Local overrides (apps/order/src/context/LaunchDarkly/overrides.json).
 */
export const useOverrides = () => {
  const { queryOverrides } = useQueryOverrides();
  const { localOverrides } = useLocalOverrides();

  const overrides = useMemo(
    () => ({ ...localOverrides, ...queryOverrides }),
    [localOverrides, queryOverrides],
  );

  return { overrides };
};

const useLocalOverrides = () => {
  const [localOverrides, setLocalOverrides] = useState(featureFlagOverrides);

  return { localOverrides, setLocalOverrides };
};

const useQueryOverrides = () => {
  const [queryOverrides, setQueryOverrides] = useState<FeatureFlagOverrides>();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const setFeatureFlagsOverrides = useCallback(async () => {
    const filteredQueryParams = await getFeatureFlagOverridesFromInitialURL();

    if (!filteredQueryParams) return;

    setQueryOverrides(filteredQueryParams);
    await deleteFeatureFlagOverridesQueryParams();
  }, []);

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    void setFeatureFlagsOverrides();
  }, [setFeatureFlagsOverrides]);

  // ─────────────────────────────────────────────────────────────────────

  return { queryOverrides };
};
